import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Map as MapIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Phone,
  Email,
  LocationOn,
  Business,
  Person,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { serviceProviderService, ServiceProvider } from '../../services/serviceProviderService';
import { requestService } from '../../services/requestService';
import AreaSelectionMap, { AreaPolygon } from '../../components/service-providers/AreaSelectionMap';
import ScheduleMap from '../../components/service-providers/ScheduleMap';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ServiceProviderOverview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load service provider
      const providerResponse = await serviceProviderService.getById(Number(id));
      setProvider(providerResponse.data);

      // Load requests for this service provider
      const requestsResponse = await requestService.getAll({
        'filters[serviceProvider][id][$eq]': id,
        populate: {
          customer: { populate: '*' },
          invoice: { populate: '*' },
        },
        sort: 'createdAt:desc',
        'pagination[pageSize]': 1000, // Get all requests
      });
      setRequests(requestsResponse.data || []);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError('Failed to load service provider data.');
    } finally {
      setLoading(false);
    }
  };

  const getProviderAreas = (provider: ServiceProvider): AreaPolygon[] => {
    const attrs = provider.attributes || provider;
    if (attrs.areaPolygons?.polygons) {
      return attrs.areaPolygons.polygons
        .filter((poly: any) => poly.coordinates && Array.isArray(poly.coordinates) && poly.coordinates.length >= 3)
        .map((poly: any) => ({
          ...poly,
          coordinates: poly.coordinates || []
        }));
    }
    return [];
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: 'warning',
      assigned: 'info',
      active: 'primary',
      invoice_sent: 'secondary',
      payment_received: 'success',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status.toLowerCase()] || 'default';
  };

  const formatCurrency = (amount: number) => {
    return `OMR ${amount.toFixed(3)}`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '-';
    }
  };

  // Calculate statistics
  const stats = {
    totalRequests: requests.length,
    pendingRequests: requests.filter(r => {
      const status = (r.attributes || r).status;
      return status === 'pending';
    }).length,
    activeRequests: requests.filter(r => {
      const status = (r.attributes || r).status;
      return ['assigned', 'active', 'invoice_sent', 'payment_received'].includes(status);
    }).length,
    completedRequests: requests.filter(r => {
      const status = (r.attributes || r).status;
      return status === 'completed';
    }).length,
    totalRevenue: requests.reduce((sum, r) => {
      const invoice = (r.attributes || r).invoice?.data?.attributes || (r.attributes || r).invoice;
      const total = invoice?.total ? parseFloat(invoice.total) : 0;
      return sum + total;
    }, 0),
  };

  // Prepare chart data
  const statusChartData = [
    { name: 'Pending', value: stats.pendingRequests },
    { name: 'Active', value: stats.activeRequests },
    { name: 'Completed', value: stats.completedRequests },
  ].filter(item => item.value > 0);

  // Daily requests chart data
  const dailyRequestsData: { [key: string]: number } = {};
  requests.forEach((request) => {
    const createdAt = (request.attributes || request).createdAt || request.createdAt;
    if (createdAt) {
      try {
        const date = new Date(createdAt);
        if (!isNaN(date.getTime())) {
          const dateKey = date.toISOString().split('T')[0];
          dailyRequestsData[dateKey] = (dailyRequestsData[dateKey] || 0) + 1;
        }
      } catch (e) {
        // Skip invalid dates
      }
    }
  });

  const dailyChartData = Object.entries(dailyRequestsData)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-30) // Last 30 days
    .map(([date, count]) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      count,
    }));

  // Revenue by month
  const monthlyRevenue: { [key: string]: number } = {};
  requests.forEach((request) => {
    const invoice = (request.attributes || request).invoice?.data?.attributes || (request.attributes || request).invoice;
    if (invoice?.total) {
      const dateString = invoice.createdAt || (request.attributes || request).createdAt;
      if (dateString) {
        try {
          const date = new Date(dateString);
          if (!isNaN(date.getTime())) {
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + parseFloat(invoice.total);
          }
        } catch (e) {
          // Skip invalid dates
        }
      }
    }
  });

  const revenueChartData = Object.entries(monthlyRevenue)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12) // Last 12 months
    .map(([month, amount]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      amount: Math.round(amount * 100) / 100,
    }));

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !provider) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Service provider not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/service-providers')} sx={{ mt: 2 }}>
          Back to Service Providers
        </Button>
      </Container>
    );
  }

  const attrs = provider.attributes || provider;
  const providerAreas = getProviderAreas(provider);

  return (
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/service-providers')}
            sx={{ mb: 2 }}
          >
            Back to Service Providers
          </Button>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
              {attrs.type === 'company' ? (
                <Business sx={{ fontSize: 48, color: 'primary.main' }} />
              ) : (
                <Person sx={{ fontSize: 48, color: 'primary.main' }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" gutterBottom>
                  {attrs.name}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    icon={<Phone />}
                    label={attrs.phone}
                    size="small"
                    variant="outlined"
                  />
                  {attrs.email && (
                    <Chip
                      icon={<Email />}
                      label={attrs.email}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {attrs.area && (
                    <Chip
                      icon={<LocationOn />}
                      label={attrs.area}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={attrs.isActive ? 'Active' : 'Inactive'}
                    color={attrs.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Stack>
              </Box>
            </Stack>

            {attrs.address && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <LocationOn sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                {attrs.address}
              </Typography>
            )}
            
            {(attrs.bankAccountNumber || attrs.bankName || attrs.bicCode || attrs.iban) && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Bank Account Information
                </Typography>
                <Stack spacing={1}>
                  {attrs.bankAccountNumber && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Account Number:</strong> {attrs.bankAccountNumber}
                    </Typography>
                  )}
                  {attrs.bankName && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Bank Name:</strong> {attrs.bankName}
                    </Typography>
                  )}
                  {attrs.bicCode && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>BIC/SWIFT:</strong> {attrs.bicCode}
                    </Typography>
                  )}
                  {attrs.iban && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>IBAN:</strong> {attrs.iban}
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </Paper>

          {/* Statistics Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Requests
                </Typography>
                <Typography variant="h4">{stats.totalRequests}</Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Active Requests
                </Typography>
                <Typography variant="h4" color="primary">
                  {stats.activeRequests}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completedRequests}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h4" color="success.main">
                  {formatCurrency(stats.totalRevenue)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Service Provider Location/Areas Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Service Areas
            </Typography>
            {providerAreas.length > 0 ? (
              <Box>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  {providerAreas.map((area) => (
                    <Chip
                      key={area.id}
                      label={area.name}
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.875rem' }}
                    />
                  ))}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  This service provider operates in {providerAreas.length} area{providerAreas.length !== 1 ? 's' : ''}: {providerAreas.map(a => a.name).join(', ')}
                </Typography>
              </Box>
            ) : attrs.area ? (
              <Box>
                <Chip
                  label={attrs.area}
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Service area: {attrs.area}
                </Typography>
              </Box>
            ) : (
              <Alert severity="info">
                No service areas defined for this service provider. Go to the service providers list to add service areas.
              </Alert>
            )}
            
            {/* Show location coordinates if available */}
            {(attrs.locationLatitude && attrs.locationLongitude) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <LocationOn sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  Location: {attrs.locationLatitude}, {attrs.locationLongitude}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab icon={<MapIcon />} iconPosition="start" label="Map" />
              <Tab icon={<ScheduleIcon />} iconPosition="start" label="Schedule" />
              <Tab icon={<AssignmentIcon />} iconPosition="start" label="Requests" />
              <Tab icon={<AssessmentIcon />} iconPosition="start" label="Charts" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Box sx={{ p: 2 }}>
                {providerAreas.length > 0 ? (
                  <AreaSelectionMap
                    initialAreas={providerAreas}
                    providerName={attrs.name}
                    readOnly={true}
                  />
                ) : (
                  <Alert severity="info">
                    No area polygons defined for this service provider. Go to the service providers list to add areas.
                  </Alert>
                )}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Box sx={{ p: 2 }}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Weekly Schedule
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    {DAYS_OF_WEEK.map((day) => {
                      const schedule = attrs.schedule?.[day];
                      const isActive = schedule?.active || false;
                      return (
                        <Card variant="outlined" key={day}>
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle1">{day}</Typography>
                              <Chip
                                label={isActive ? 'Active' : 'Inactive'}
                                color={isActive ? 'success' : 'default'}
                                size="small"
                              />
                            </Stack>
                            {isActive && schedule?.from && schedule?.to && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {schedule.from} - {schedule.to}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Paper>
                {attrs.areaPolygons?.polygons && attrs.areaPolygons.polygons.length > 0 && (
                  <ScheduleMap providers={[provider]} selectedDay="all" />
                )}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Box sx={{ p: 2 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Request #</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Service Type</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No requests found
                          </TableCell>
                        </TableRow>
                      ) : (
                        requests.map((request) => {
                          const reqAttrs = request.attributes || request;
                          const customer = reqAttrs.customer?.data?.attributes || reqAttrs.customer;
                          const invoice = reqAttrs.invoice?.data?.attributes || reqAttrs.invoice;
                          return (
                            <TableRow key={request.id}>
                              <TableCell>{reqAttrs.requestNumber || `#${request.id}`}</TableCell>
                              <TableCell>
                                {customer?.name || customer?.firstName
                                  ? `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim() || customer?.name
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={reqAttrs.status || 'pending'}
                                  color={getStatusColor(reqAttrs.status || 'pending') as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{reqAttrs.serviceType || '-'}</TableCell>
                              <TableCell>{formatDate(reqAttrs.createdAt)}</TableCell>
                              <TableCell>
                                {invoice?.total ? formatCurrency(parseFloat(invoice.total)) : '-'}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  onClick={() => navigate(`/requests/${request.id}`)}
                                >
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                  {statusChartData.length > 0 && (
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Request Status Distribution
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={statusChartData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {statusChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {dailyChartData.length > 0 && (
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Daily Requests (Last 30 Days)
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={dailyChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {revenueChartData.length > 0 && (
                    <Card sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Monthly Revenue (Last 12 Months)
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={revenueChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value: any) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="amount" fill="#8884d8" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Box>
            </TabPanel>
          </Paper>
        </Box>
      </Container>
  );
};

export default ServiceProviderOverview;
